import React, { useState } from 'react';
import { Icon, InputField, Typography } from '@saviynt/design-system';

import { useInvertedLocalize } from '../utils/localization';

type CopyStates = 'ready' | 'in-progress' | 'error' | 'success';

interface Props {
  id: string;
  label: string;
  isReadOnly?: boolean;
  value: string;
  setValue?: (value: string) => void;
}

/** InputField with built-in copy functionality. */
export default function CopyInputField({
  id,
  label,
  isReadOnly = false,
  value,
  setValue = () => {},
}: Props): JSX.Element {
  const l = useInvertedLocalize();
  const [copyState, setCopyState] = useState<CopyStates>('ready');

  const handleCopy = () => {
    setCopyState('in-progress');
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopyState('success');
      })
      .catch(() => {
        setCopyState('error');
      })
      .finally(() => {
        setTimeout(() => setCopyState('ready'), 2000);
      });
  };

  const getCopyIcon = () => {
    switch (copyState) {
      case 'success':
        return 'Check';
      case 'error':
        return 'AlertCriticalOutline';
      case 'in-progress':
        return 'Progress';
      default:
        return 'Copy';
    }
  };

  return (
    <InputField
      name={id}
      placeholder=''
      value={value}
      setValue={setValue}
      isReadOnly={isReadOnly}
      readOnlyKind='boxed'
      label={
        <Typography kind='label' htmlFor={id}>
          {label}
        </Typography>
      }
      suffixButtonIcon={
        <div title={l('Copy to Clipboard')}>
          <Icon kind={getCopyIcon()} />
        </div>
      }
      suffixButtonIconLabel={l('Copy to Clipboard')}
      suffixOnClick={() => handleCopy()}
    />
  );
}
